import React from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Card, Text, Button, Badge as PolarisBadge, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import PrettyDate from "components/format/PrettyDate";
import {
  fetchUserAssignedRoles,
  UserAssignedRole,
  FetchUserAssignedRolesResponse,
} from "services/work-security.service";
import Loading from "components/loading/Loading";
import UserRolesPanel from "features/work-security-approvals/UserRolesPanel";
import { API_URL } from "../../constants";

// The roles are cached for the whole session, so moving between the profile
// pages does not fetch them again.
type ProfileRolesCache = {
  scope: string;
  roles: UserAssignedRole[];
};

let profileRolesCache: ProfileRolesCache | null = null;
let profileRolesInflight: Promise<ProfileRolesCache> | null = null;
let profileRolesInflightScope: string | null = null;

function profileRolesScope(companyId: string, userId: string) {
  return `${companyId}:${userId}`;
}

async function loadProfileRoles(
  companyId: string,
  userId: string,
  force = false
): Promise<ProfileRolesCache> {
  const scope = profileRolesScope(companyId, userId);

  if (!force && profileRolesCache?.scope === scope) {
    return profileRolesCache;
  }

  if (!force && profileRolesInflight && profileRolesInflightScope === scope) {
    return profileRolesInflight;
  }

  profileRolesInflightScope = scope;
  profileRolesInflight = fetchUserAssignedRoles(companyId, userId).then(
    (data: FetchUserAssignedRolesResponse) => {
      const cached: ProfileRolesCache = {
        scope,
        roles: data.roles || [],
      };
      profileRolesCache = cached;
      profileRolesInflight = null;
      profileRolesInflightScope = null;
      return cached;
    },
    (error) => {
      // A failed request must not be handed to the next caller as if it were still running.
      profileRolesInflight = null;
      profileRolesInflightScope = null;
      throw error;
    }
  );

  return profileRolesInflight;
}

/** What the roles say about unsigned documents: `count` is null until they have loaded or when they could not be. */
interface UnsignedDocumentsAnswer {
  count: number | null;
  hasFailed: boolean;
}

const NO_ANSWER_YET: UnsignedDocumentsAnswer = { count: null, hasFailed: false };

function unsignedDocumentsAnswer(loading: Promise<ProfileRolesCache>): Promise<UnsignedDocumentsAnswer> {
  return loading.then(
    ({ roles }) => {
      const documents = roles.flatMap((item) => item.documents || []);
      const unsigned = documents.filter((doc) => doc.signature?.status !== "signed");
      return { count: unsigned.length, hasFailed: false };
    },
    () => ({ count: null, hasFailed: true })
  );
}

/**
 * How many role documents the person has not signed yet, or that the roles could not be
 * loaded (`hasFailed`; `reload` asks the server again). It reads the same cache as the list
 * below, so it sends no request of its own. Signing a document refreshes the cabinet summary,
 * so the summary is passed in as `refreshWhen`: the count then reads the cache again. A manual
 * Refresh inside UserAssignedRoles updates the cache, but the count follows it only when the
 * summary changes.
 */
export function useUnsignedRoleDocumentCount(companyId: string, userId: string, refreshWhen: unknown) {
  const scope = profileRolesScope(companyId, userId);
  const [answer, setAnswer] = React.useState({ scope, ...NO_ANSWER_YET });

  React.useEffect(() => {
    let cancelled = false;
    unsignedDocumentsAnswer(loadProfileRoles(companyId, userId)).then((next) => {
      if (!cancelled) setAnswer({ scope, ...next });
    });
    return () => {
      cancelled = true;
    };
  }, [companyId, userId, scope, refreshWhen]);

  // The scope on screen now, so a retry that answers after a switch of company or person
  // does not overwrite the new scope's answer.
  const currentScope = React.useRef(scope);
  currentScope.current = scope;
  const reload = () => {
    const reloadScope = scope;
    setAnswer({ scope: reloadScope, ...NO_ANSWER_YET });
    unsignedDocumentsAnswer(loadProfileRoles(companyId, userId, true)).then((next) => {
      if (currentScope.current === reloadScope) setAnswer({ scope: reloadScope, ...next });
    });
  };

  // An answer given for another company or person is no answer here, not even for one render.
  const { count, hasFailed } = answer.scope === scope ? answer : NO_ANSWER_YET;
  return { count, hasFailed, reload };
}

// The buttons of a role document. On a phone «Pasirašyti» is a full-width button under the
// status, and «Peržiūrėti» and «Atsisiųsti» share the line under it.
const DOCUMENT_BUTTON =
  "px-2.5 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium cursor-pointer max-md:order-2 max-md:min-h-11 max-md:flex-1 max-md:text-sm";
const SIGN_BUTTON =
  "px-2.5 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-xs font-semibold cursor-pointer max-md:order-1 max-md:min-h-11 max-md:basis-full max-md:text-sm";

/** Assigned safety roles list. `hideHeading` drops its own title for a page that already names the block. */
export default function UserAssignedRoles({
  companyId,
  userId,
  hideHeading = false,
}: {
  companyId: string;
  userId: string;
  hideHeading?: boolean;
}) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [roles, setRoles] = React.useState<UserAssignedRole[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  // View only: the profile is the employee's own page. Individual pareigybės
  // are added/removed on the darbuotojo kortelė (Personalas → Tvarkos tab).

  const loadRoles = React.useCallback(
    async (force = false) => {
      try {
        setLoading(true);
        const data = await loadProfileRoles(companyId, userId, force);
        setLoadError(false);
        setRoles(data.roles || []);
      } catch (err) {
        console.error("Failed to load user assigned roles", err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [companyId, userId]
  );

  const markDocumentSigned = React.useCallback(
    (fileId: string) => {
      setRoles((currentRoles) => {
        const updatedRoles = currentRoles.map((item) => ({
          ...item,
          documents: (item.documents || []).map((docItem) =>
            docItem.fileId === fileId
              ? {
                  ...docItem,
                  signature: {
                    ...docItem.signature,
                    status: "signed" as const,
                    signedAt:
                      docItem.signature?.signedAt || new Date().toISOString(),
                  },
                }
              : docItem
          ),
        }));
        const scope = profileRolesScope(companyId, userId);
        if (profileRolesCache?.scope === scope) {
          profileRolesCache = { scope, roles: updatedRoles };
        }
        return updatedRoles;
      });
    },
    [companyId, userId]
  );

  React.useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  if (loading && roles.length === 0) {
    return <Loading />;
  }

  if (loadError && roles.length === 0) {
    return (
      <Card roundedAbove="sm">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text as="p" variant="bodyMd" tone="critical">
            {t("assigned_roles_load_failed", { defaultValue: "Unable to load assigned roles. Please try again." })}
          </Text>
          <Button onClick={() => loadRoles(true)} variant="plain">
            {t("retry", { defaultValue: "Retry" })}
          </Button>
        </div>
      </Card>
    );
  }

  if (roles.length === 0) {
    return (
      <Card roundedAbove="sm">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text as="p" variant="bodyMd">
            {t("no_assigned_roles", { defaultValue: "No assigned roles found." })}
          </Text>
          <Button onClick={() => loadRoles(true)} variant="plain">
            {t("refresh", { defaultValue: "Refresh" })}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {loadError && (
        <Banner tone="critical">
          {t("assigned_roles_load_failed", { defaultValue: "Unable to load assigned roles. Please try again." })}
        </Banner>
      )}
      <Card roundedAbove="sm">
        <div
          style={{
            display: "flex",
            justifyContent: hideHeading ? "flex-end" : "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          {!hideHeading && (
            <Text as="h2" variant="headingMd">
              {t("assigned_work_security_roles", { defaultValue: "Assigned Roles" })}
            </Text>
          )}
          <Button onClick={() => loadRoles(true)} variant="plain">
            {t("refresh", { defaultValue: "Refresh" })}
          </Button>
        </div>

        {/* Same list rendering as the employee card and the edit form
            (UserRolesPanel), read only, with this profile's own document
            actions (self-service view/sign/download) under each role. */}
        <UserRolesPanel
          companyId={companyId}
          userId={userId}
          readOnly
          roles={roles}
          renderRoleExtra={(item) =>
            (item.documents || []).length === 0 ? (
              <p className="text-xs text-zinc-400 italic mt-1">
                {t("no_document_yet_for_role", { defaultValue: "No procedure document generated for this role yet." })}
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {(item.documents || []).map((docItem) => {
                  const isSigned = docItem.signature?.status === "signed";
                  const fileId = docItem.fileId;
                  return (
                    <div
                      key={docItem.id}
                      className="bg-zinc-50 p-3 rounded-lg border border-zinc-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-zinc-700 truncate" title={docItem.fileName}>
                          {docItem.fileName}
                        </p>
                        <p className="text-xs md:text-[10px] text-zinc-400 mt-0.5">
                          {isSigned && docItem.signature?.signedAt ? (
                            <>
                              {t("signed_on", { defaultValue: "Signed on" })}{" "}
                              <PrettyDate dateString={docItem.signature.signedAt} showTime={true} />
                            </>
                          ) : (
                            <>
                              {t("generated_on", { defaultValue: "Generated on" })}{" "}
                              <PrettyDate dateString={docItem.createdAt} />
                            </>
                          )}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 max-md:flex-wrap md:shrink-0">
                        {isSigned ? (
                          <PolarisBadge tone="success">
                            {t("signed", { defaultValue: "Signed" })}
                          </PolarisBadge>
                        ) : (
                          <PolarisBadge tone="attention">
                            {t("pending_signature", { defaultValue: "Pending Signature" })}
                          </PolarisBadge>
                        )}
                        <button
                          type="button"
                          className={DOCUMENT_BUTTON}
                          onClick={() => {
                            dispatch(setModalxComponent("PdfView"));
                            dispatch(setModalxComponentProps({ id: fileId, companyId }));
                            dispatch(setIsModalxOpen(true));
                          }}
                        >
                          {t("view", { defaultValue: "View" })}
                        </button>
                        <button
                          type="button"
                          className={DOCUMENT_BUTTON}
                          onClick={() => window.open(`${API_URL}/${companyId}/files/download/${fileId}`, "_blank")}
                        >
                          {t("download", { defaultValue: "Download" })}
                        </button>
                        {!isSigned && (
                          <button
                            type="button"
                            className={SIGN_BUTTON}
                            onClick={() => {
                              dispatch(setModalxComponent("ClickToSignConfirm"));
                              dispatch(
                                setModalxComponentProps({
                                  fileId,
                                  companyId,
                                  onSuccess: () => markDocumentSigned(fileId),
                                })
                              );
                              dispatch(setIsModalxOpen(true));
                            }}
                          >
                            {t("sign", { defaultValue: "Sign" })}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          }
        />
      </Card>
    </div>
  );
}
